// The one start dialog's rules (./StartWorkDialog.tsx), pure so
// startWorkModel.selftest.ts runs them. One dialog starts work now ("Give
// a task", or "Run <workflow>") or later ("New schedule", "Edit: …"): Who
// does it · When · What · Name and Report (later only).
//
//   worker  what the Who dropdown holds: a template (an agent or a
//           subagent), "team", "workflow:<slug>" or "message" (a reminder)
//   from    the standard agent whose chat a schedule's answer (or a
//           reminder) comes from: Report's "From"
//
// A schedule stores who does it as the store has it (scheduleFormModel.ts):
// a standard agent that reports in its own chat, or reports nowhere, is
// `me` (its own row, owner = itself); any other agent or subagent is
// `agent` with its template as ref and From as owner. A saved row whose
// worker and From are unchanged keeps its stored who exactly, so saving it
// untouched sends what it sent before. A question is never `me` (an agent
// cannot ask itself): From that nobody picked is another agent then, a
// quiet question keeps From as the one who asks, and a question whose
// worker is the From someone picked is refused in words, never turned
// into a task.

import type { UnifiedSchedule } from "@/api/schedulesUnified";
import { workflowErrorCode } from "@/api/workflows";
import { MAX_BRIEF, MAX_TITLE, canAsk, minutesRule, taskErrorText, type GiveWorkerKind } from "@/features/tasks/taskModel";
import { ApiError } from "@/lib/api";
import { minutesWords } from "@/features/workflows/time";
import { routineDisplayName } from "@/lib/routineNames";

import { chainErrorText } from "../schedules/unified/chainModel";
import { emptyForm, minutesLimit, needsOwner, type FormSeed, type ReportChoice, type ScheduleForm, type WhenChoice, type WhoChoice } from "../schedules/unified/scheduleFormModel";

export type StartWhen = "now" | WhenChoice;
export type WorkerKind = "none" | "agent" | "subagent" | "team" | "workflow" | "message";

/** What a door opens the dialog with: the worker, the When, the reporting agent. */
export interface StartPreset {
  to?: string;
  when?: StartWhen;
  owner?: string;
  /** What to ask, filled in (a task's "Run again": its title, brief, time limit, a question or not, the browser). */
  again?: { title: string; brief: string; minutes?: number; question?: boolean; needsBrowser?: boolean };
}

/** The form a door opens with: empty, or a task's own words for "Run again". */
export function presetForm(preset: StartPreset, now: Date = new Date()): ScheduleForm {
  const form = emptyForm({}, now);
  const a = preset.again;
  if (!a) return form;
  return {
    ...form,
    title: a.title,
    brief: a.brief,
    minutes: a.minutes && a.minutes > 0 ? a.minutes : form.minutes,
    question: !!a.question,
    needsBrowser: !!a.needsBrowser,
  };
}

/** A schedule's seed (a page's "New schedule") as a preset: it opens at Repeats unless it says otherwise. */
export function presetFromSeed(seed: FormSeed): StartPreset {
  const to =
    seed.who === "agent" ? seed.ref : seed.who === "workflow" ? (seed.ref ? `workflow:${seed.ref}` : "") : seed.who === "team" || seed.who === "message" ? seed.who : seed.owner;
  const out: StartPreset = { when: seed.when ?? "cron" };
  if (to) out.to = to;
  if (seed.owner) out.owner = seed.owner;
  return out;
}

export function workerKind(worker: string, isStandard: (template: string) => boolean): WorkerKind {
  if (!worker) return "none";
  if (worker === "team" || worker === "message") return worker;
  if (worker.startsWith("workflow:")) return "workflow";
  return isStandard(worker) ? "agent" : "subagent";
}

/** The give's own word for the worker (taskModel.ts). */
export function giveKind(kind: WorkerKind): GiveWorkerKind {
  return kind === "team" || kind === "workflow" || kind === "subagent" ? kind : "agent";
}

/** The worker a door opens with: its own, else (for later) the reporting agent, else nobody yet. */
export function defaultWorker(preset: StartPreset, firstStandard: string): string {
  if (preset.to) return preset.to;
  return preset.when && preset.when !== "now" ? preset.owner ?? firstStandard : "";
}

/** The reporting agent a door opens with: its own, else the worker when it is a standard agent, else the first one. */
export function defaultFrom(preset: StartPreset, worker: string, isStandard: (template: string) => boolean, firstStandard: string): string {
  return preset.owner ?? (worker && isStandard(worker) ? worker : firstStandard);
}

/** From when nobody picked it: a question is asked by another agent than its worker (the first other standard one). */
export function askerFor(followed: string, worker: string, question: boolean, standard: readonly string[]): string {
  if (!question || followed !== worker) return followed;
  return standard.find((t) => t !== worker) ?? followed;
}

/** Who does a saved row, as the Who dropdown holds it. */
export function workerOfSchedule(row: Pick<UnifiedSchedule, "who" | "owner">): string {
  const { kind, ref } = row.who;
  if (kind === "me") return row.owner;
  if (kind === "workflow") return `workflow:${ref}`;
  if (kind === "team" || kind === "message") return kind;
  return ref;
}

export interface StoredWho {
  who: WhoChoice;
  ref: string;
  owner: string;
}

/** A saved row's stored who, with the worker and From the dialog opened it with. */
export interface OriginalWho extends StoredWho {
  worker: string;
  from: string;
}

/** How a schedule stores the worker and From (see the top of this file). */
export function storedWho(worker: string, from: string, report: ReportChoice, isStandard: (template: string) => boolean, original?: OriginalWho | null, question = false): StoredWho {
  if (original && original.worker === worker && original.from === from) {
    const kept: StoredWho = { who: original.who, ref: original.ref, owner: original.owner };
    // a row saved without an owner gets From once its answer has to come from someone
    return !kept.owner && needsOwner({ who: kept.who, report }) ? { ...kept, owner: from } : kept;
  }
  if (worker === "team" || worker === "message") return { who: worker, ref: "", owner: from };
  if (worker.startsWith("workflow:")) return { who: "workflow", ref: worker.slice("workflow:".length), owner: from };
  if (worker && isStandard(worker) && (worker === from || (report === "quiet" && !question))) return { who: "me", ref: "", owner: worker };
  return { who: "agent", ref: worker, owner: from };
}

/**
 * "A task | A quick question": for an agent or a subagent; in a schedule, only one that is not reporting in its
 * own chat, unless the form already holds a question (then it stays, and questionProblem says why it cannot go).
 */
export function showsQuestion(now: boolean, kind: WorkerKind, stored: Pick<StoredWho, "who" | "ref">, question = false): boolean {
  return now ? kind === "agent" || kind === "subagent" : (stored.who === "agent" && !!stored.ref) || (question && kind === "agent");
}

/** A schedule's question to the agent it reports from (stored as `me`): refused, never turned into a task. */
export function questionProblem({ now, question, stored, report }: { now: boolean; question: boolean; stored: Pick<StoredWho, "who">; report: ReportChoice }): string | null {
  if (now || !question || stored.who !== "me") return null;
  return report === "quiet" ? "A question goes to another agent: report it in chat from another agent, or make it a task." : "A question goes to another agent: choose another From, or make it a task.";
}

/** The schedule form the parts and the body read: the stored who, and a question only where it is offered. */
export function formFor(form: ScheduleForm, stored: StoredWho, question: boolean): ScheduleForm {
  return { ...form, ...stored, question: form.question && question };
}

// ─── when ───

export interface WhenOption {
  value: StartWhen;
  label: string;
  disabled: boolean;
}

const WHEN_LABEL: Readonly<Record<StartWhen, string>> = { now: "Now", at: "Once", cron: "Repeats", every: "Every…", after: "After a workflow" };

/**
 * The When choices: Now only where tasks are on, never for a reminder, a saved row or a dialog that is for
 * later only; the rest only on the one schedules store. A workflow runs on a timetable or after another one.
 */
export function whenChoices({ unified, tasksOn, editing, laterOnly, worker }: { unified: boolean; tasksOn: boolean; editing: boolean; laterOnly: boolean; worker: WorkerKind }): WhenOption[] {
  const now = tasksOn && !editing && !laterOnly && worker !== "message";
  const order: StartWhen[] = unified ? ["now", "at", "cron", "every", "after"] : ["now"];
  return order
    .filter((v) => v !== "now" || now)
    .map((value) => ({ value, label: WHEN_LABEL[value], disabled: worker === "workflow" ? value === "at" || value === "every" : value === "after" }));
}

/** The When kept when the choices change: the same, else Repeats, else the first one left. */
export function fitWhen(when: StartWhen, choices: readonly WhenOption[]): StartWhen {
  const ok = (v: StartWhen) => choices.some((c) => c.value === v && !c.disabled);
  if (ok(when)) return when;
  if (ok("cron")) return "cron";
  return choices.find((c) => !c.disabled)?.value ?? when;
}

// ─── words ───

export function dialogTitle({ when, editing, workflowName, workerName }: { when: StartWhen; editing?: string | null; workflowName?: string | null; workerName?: string | null }): string {
  if (editing) return `Edit: ${editing}`;
  if (when !== "now") return "New schedule";
  if (workflowName) return `Run ${workflowName}`;
  return workerName ? `Give ${workerName} a task` : "Give a task";
}

/** The dialog opened on a named agent or subagent, to start now: only the words and Start, the rest behind Change. */
export function opensCompact(o: { presetTo?: string | null; editing: boolean; laterOnly: boolean; when: StartWhen | undefined; kind: string }): boolean {
  return !!o.presetTo && !o.editing && !o.laterOnly && (o.when ?? "now") === "now" && (o.kind === "agent" || o.kind === "subagent");
}

/** The quiet line under the words: "Now · 1 h", the browser when it was asked for. */
export function compactFacts(minutes: number, needsBrowser: boolean): string {
  return ["Now", Number.isFinite(minutes) ? minutesWords(minutes) : "", needsBrowser ? "the browser" : ""].filter(Boolean).join(" · ");
}

export function submitLabel({ when, editing, conflict }: { when: StartWhen; editing: boolean; conflict: boolean }): string {
  if (conflict) return "Save anyway";
  if (editing) return "Save";
  return when === "now" ? "Start" : "Create schedule";
}

// ─── starting now ───

export type NowField = "ref" | "brief" | "title" | "minutes" | "params";

/**
 * What stops a start now, by field ({} = it can go). A workflow's settings stop it while a required one is empty
 * (`missing`) or the settings form flags a value (`badParams`); each field says its own problem.
 */
export function nowProblems(f: { kind: WorkerKind; brief: string; title: string; minutes: number; question: boolean; missing: readonly string[]; badParams?: boolean; cap?: number | null }): Partial<Record<NowField, string>> {
  const out: Partial<Record<NowField, string>> = {};
  if (f.kind === "none" || f.kind === "message") out.ref = "Choose who does it.";
  if (f.kind === "workflow") {
    if (f.missing.length || f.badParams) out.params = "Fill in the required settings.";
    return out;
  }
  const brief = f.brief.trim();
  if (!brief) out.brief = "Say what to do.";
  else if (brief.length > MAX_BRIEF) out.brief = `At most ${MAX_BRIEF.toLocaleString("en-GB")} characters.`;
  if (f.title.trim().length > MAX_TITLE) out.title = `At most ${MAX_TITLE} characters.`;
  const worker = giveKind(f.kind);
  const rule = minutesRule(f.question && canAsk(worker) ? "question" : "task", worker);
  const max = worker === "team" || !f.cap ? rule.max : Math.min(rule.max, f.cap);
  if (rule.shown && rule.fixed === null && (!Number.isInteger(f.minutes) || f.minutes < rule.min || f.minutes > max)) out.minutes = `${rule.min} to ${max} minutes.`;
  return out;
}

/**
 * The time limit as the form shows and sends it, for a worker that may run at most `cap` minutes (null: not
 * known yet): one the operator has not typed (the default, 60) comes down to the cap; a typed one stands.
 */
export function fitMinutes(minutes: number, cap: number | null, typed: boolean): number {
  return cap !== null && !typed && Number.isFinite(minutes) && minutes > cap ? cap : minutes;
}

/** A schedule's time limit over what its worker may run (`cap`), in the field's words; null when it fits. */
export function laterMinutesProblem(f: Pick<ScheduleForm, "who" | "question" | "minutes">, cap: number | null): string | null {
  const limit = f.who === "team" ? null : minutesLimit(f);
  if (!limit || cap === null || !Number.isInteger(f.minutes) || f.minutes <= cap) return null;
  return `${limit.min} to ${Math.min(limit.max, cap)} minutes.`;
}

/** The workflow's required settings that have no default and no value yet (their names). */
export function missingRequired(params: readonly { name: string; required?: boolean | null; default?: unknown }[], values: Readonly<Record<string, unknown>>): string[] {
  const empty = (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
  return params.filter((p) => p.required && empty(p.default) && empty(values[p.name])).map((p) => p.name);
}

/** Only the settings the workflow declares (the server refuses any other). */
export function onlyDeclared(values: Readonly<Record<string, unknown>>, params: readonly { name: string }[]): Record<string, unknown> {
  const names = new Set(params.map((p) => p.name));
  return Object.fromEntries(Object.entries(values).filter(([k]) => names.has(k)));
}

/** The settings as a workflow is given them, compared whatever order their keys came in. */
function sameParams(a: Readonly<Record<string, unknown>>, b: Readonly<Record<string, unknown>>): boolean {
  const keys = (o: Readonly<Record<string, unknown>>) => Object.keys(o).filter((k) => o[k] !== undefined).sort();
  const ka = keys(a);
  return ka.length === keys(b).length && ka.every((k, i) => k === keys(b)[i] && JSON.stringify(a[k]) === JSON.stringify(b[k]));
}

/**
 * Whether the dialog holds text the operator typed and a close would lose (Modal's "Discard?"): the brief,
 * the title, a name they typed, a workflow's settings. The When and Report choices are not typed text.
 */
export function typedChanged(start: Pick<ScheduleForm, "brief" | "title" | "name" | "params">, now: Pick<ScheduleForm, "brief" | "title" | "name" | "params">, nameTyped: boolean): boolean {
  if (now.brief.trim() !== start.brief.trim() || now.title.trim() !== start.title.trim()) return true;
  if (nameTyped && now.name.trim() !== start.name.trim()) return true;
  return !sameParams(start.params, now.params);
}

/** "More options" on a Team task: the full brief, carrying what the dialog already holds (the goal, a title, the time limit). */
export function moreOptionsPath(base: string, form: Pick<ScheduleForm, "brief" | "title" | "minutes">): string {
  const q = new URLSearchParams({ goal: form.brief.trim() });
  if (form.title.trim()) q.set("title", form.title.trim());
  if (Number.isInteger(form.minutes) && form.minutes > 0) q.set("minutes", String(form.minutes));
  return `${base}?${q.toString()}`;
}

/** One key per dialog open: a send repeated after a dropped answer gets the task the first one made. */
export function newIdempotencyKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

const SCHEDULE_REFUSAL: Readonly<Record<string, string>> = {
  conflict: "Another schedule already runs at that time, done by the same one and reported to the same place. Change that one, or say why both are needed.",
  force_needs_reason: "Say why both are needed.",
};

/** The schedules a conflict names: the server's message opens with them ("a, b already runs on that timetable …"). */
export function clashNames(message: unknown): string[] {
  if (typeof message !== "string") return [];
  const at = message.indexOf(" already runs");
  if (at <= 0 || at > 400) return [];
  return message.slice(0, at).split(", ").map((n) => n.trim()).filter(Boolean);
}

function conflictText(names: readonly string[], titleOf?: (name: string) => string | undefined): string {
  // named as the Schedules list names them
  const quoted = names.map((n) => `“${titleOf?.(n) || routineDisplayName(n) || n}”`);
  if (quoted.length === 1) return `${quoted[0]} already runs at that time, done by the same one and reported to the same place. Change that one, or say why both are needed.`;
  if (quoted.length > 1) return `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]} already run at that time, done by the same one and reported to the same place. Change one of them, or say why this one is needed too.`;
  return SCHEDULE_REFUSAL.conflict;
}

/**
 * Whether the dialog shows the team's install beside its refusal: a Team task refused because the team is not
 * installed, whether it was to start now (the task's give) or on a schedule (the schedule's save).
 */
export function teamMissingRefusal(now: boolean, giveError: unknown, saveError: unknown): boolean {
  return workflowErrorCode(now ? giveError : saveError) === "team_missing";
}

/**
 * A schedule's refusal in words: a second one on the same slot (named as `titleOf` names a saved row, else from
 * its name), a chain's own (a loop names the workflows in it), else the tasks' wording.
 */
export function scheduleErrorText(error: unknown, titleOf?: (name: string) => string | undefined): string {
  const code = workflowErrorCode(error);
  const body = error instanceof ApiError ? (error.body as { detail?: unknown } | null) : null;
  const detail = body?.detail && typeof body.detail === "object" && !Array.isArray(body.detail) ? (body.detail as Record<string, unknown>) : {};
  if (code === "conflict") return conflictText(clashNames(detail.message), titleOf);
  if (code && SCHEDULE_REFUSAL[code]) return SCHEDULE_REFUSAL[code];
  return chainErrorText(code, detail) ?? taskErrorText(error);
}
